/**
 * Logica pura del pannello "Info gruppo": chi sono io nel gruppo, ordinamento dei
 * partecipanti, lettura degli aggiornamenti dal vivo mandati dal core.
 */
import type { GroupMember, GroupRole } from '@/lib/services/whatsapp/wahaGroups';

export type MyGroupRole = 'admin' | 'member' | 'unknown';

export const digitsOf = (jid: string | null | undefined): string =>
  (jid || '').split('@')[0].replace(/\D/g, '');

/** Numero @c.us di un partecipante: dal dato WAHA o dalla mappa @lid -> @c.us gia' nota. */
export function phoneOf(m: Pick<GroupMember, 'id' | 'phoneJid'>, lidMap?: Map<string, string>): string | null {
  if (m.phoneJid) return m.phoneJid;
  if (m.id.endsWith('@c.us')) return m.id;
  return lidMap?.get(m.id) || null;
}

const isAdminRole = (r: GroupRole) => r === 'admin' || r === 'superadmin';

/**
 * Il nostro numero e' amministratore? 'unknown' se non lo troviamo fra i partecipanti
 * (succede coi gruppi che mostrano solo id @lid): in quel caso si lascia provare.
 */
export function myGroupRole(members: GroupMember[], ownNumber: string | null | undefined, lidMap?: Map<string, string>): MyGroupRole {
  const own = digitsOf(ownNumber);
  if (own.length < 7) return 'unknown';
  const me = members.find((m) => {
    const d = digitsOf(phoneOf(m, lidMap));
    return d.length >= 7 && (d === own || d.endsWith(own) || own.endsWith(d));
  });
  if (!me) return 'unknown';
  return isAdminRole(me.role) ? 'admin' : 'member';
}

export function isMe(m: GroupMember, ownNumber: string | null | undefined, lidMap?: Map<string, string>): boolean {
  const own = digitsOf(ownNumber);
  const d = digitsOf(phoneOf(m, lidMap));
  return own.length >= 7 && d.length >= 7 && (d === own || d.endsWith(own) || own.endsWith(d));
}

const RANK: Record<GroupRole, number> = { superadmin: 0, admin: 1, participant: 2, left: 3 };

export function sortMembers(list: GroupMember[], nameOf: (m: GroupMember) => string): GroupMember[] {
  return [...list].sort((a, b) => RANK[a.role] - RANK[b.role] || nameOf(a).localeCompare(nameOf(b), 'it'));
}

export function adminHint(role: MyGroupRole): string | null {
  if (role === 'member') return 'Solo gli amministratori del gruppo possono cambiare nome, descrizione, partecipanti e link. Il tuo numero non è amministratore.';
  if (role === 'unknown') return 'Non riesco a verificare se il tuo numero è amministratore: se non lo è, WhatsApp rifiuterà le modifiche.';
  return null;
}

// ---------- Aggiornamenti dal vivo (broadcast 'group_update' su groups-{org}) ----------

export interface GroupUpdate {
  groupId: string;
  configId: string | null;
  /** join | leave | promote | demote (eventi partecipanti) o null. */
  type: string | null;
  subject: string | null;
  participants: Array<{ id: string; role: string | null }> | null;
}

/**
 * Forma reale del core (handlers/group.ts): { event, configId, groupId, type, subject,
 * participants: [{id, role}], timestamp }. Si accettano anche group/name come alias.
 */
export function parseGroupUpdate(payload: unknown): GroupUpdate | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const groupId = [p.groupId, p.group].find((v): v is string => typeof v === 'string' && v.endsWith('@g.us'));
  if (!groupId) return null;
  const subject = [p.subject, p.name].find((v): v is string => typeof v === 'string' && !!v.trim());
  const list = Array.isArray(p.participants)
    ? p.participants
        .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && typeof (x as Record<string, unknown>).id === 'string')
        .map((x) => ({ id: x.id as string, role: typeof x.role === 'string' ? x.role : null }))
    : null;
  return {
    groupId,
    configId: typeof p.configId === 'string' ? p.configId : null,
    type: typeof p.type === 'string' ? p.type : null,
    subject: subject ? subject.trim() : null,
    participants: list,
  };
}

const sameMember = (m: GroupMember, id: string) =>
  m.id === id || m.phoneJid === id || (digitsOf(id).length >= 7 && digitsOf(m.phoneJid || m.id) === digitsOf(id));

/** Applica un evento partecipanti all'elenco in memoria. Tipo sconosciuto: elenco invariato. */
export function applyParticipantsUpdate(list: GroupMember[], u: GroupUpdate): GroupMember[] {
  if (!u.participants || u.participants.length === 0) return list;
  const ids = u.participants.map((p) => p.id);
  switch (u.type) {
    case 'join': {
      const added = ids
        .filter((id) => !list.some((m) => sameMember(m, id)))
        .map((id): GroupMember => ({ id, phoneJid: id.endsWith('@c.us') ? id : null, role: 'participant' }));
      return [...list, ...added];
    }
    case 'leave':
      return list.filter((m) => !ids.some((id) => sameMember(m, id)));
    case 'promote':
    case 'demote':
      return list.map((m) => (ids.some((id) => sameMember(m, id))
        ? { ...m, role: u.type === 'promote' ? (m.role === 'superadmin' ? 'superadmin' : 'admin') : 'participant' }
        : m));
    default:
      return list;
  }
}
